import { map } from 'rxjs/operators';
import { AngularFirestore, DocumentReference } from '@angular/fire/firestore';
import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/functions';
import { AngularFireStorage } from '@angular/fire/storage';
import { ErrorMonitoringService } from '../error-monitoring/error-monitoring.service';
import { Observable } from 'rxjs/internal/Observable';
import { FileTransport } from 'src/app/interfaces/general-objects';
import * as moment from 'moment-timezone';

@Injectable({
  providedIn: 'root'
})
export class ServiceService {

  uploadPercent: Observable<number>;
  downloadURL: Observable<string>;

  servicesRef = 'services';
  userLocationRef = 'userLocations';

  constructor(
    private angularFirestore: AngularFirestore,
    private angularFireFunctions: AngularFireFunctions,
    private storage: AngularFireStorage,
    private errorMonitoringService: ErrorMonitoringService
  ) { }

  // agrega un servicio
  addService(service: any): Promise<DocumentReference> {
    return this.angularFirestore.collection<any>(this.servicesRef).add(service);
  }

  // obtiene los servicios de un cliente
  getUserServices(email: string, date: string) {
    return this.angularFirestore.collection<any>(
      this.servicesRef,
      ref => ref.where('client.email', '==', email)
      .where('dates.serviceDate', '>=', date)
      .orderBy(`dates.serviceDate`, 'asc').limit(10)
    ).snapshotChanges().pipe(
      map( services => {
        return services.map(service => {
          const docRef = service.payload.doc;
          const data = service.payload.doc.data();
          const id = service.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene los eventos
  getEvents() {
    return this.angularFirestore.collection<any>(
      'events',
      ref => ref.where('date', '>=', moment().tz('America/Bogota').format('DD-MM-YYYY'))
      .orderBy(`date`, 'asc')
    ).snapshotChanges().pipe(
      map( events => {
        return events.map( event => {
          const docRef = event.payload.doc;
          const data = event.payload.doc.data();
          const id = event.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene mas servicios de un cliente
  getMoreUserServices(email: string, date: string, doc: any) {
    return this.angularFirestore.collection<any>(
      this.servicesRef,
      ref => ref.where('client.email', '==', email).where('dates.serviceDate', '>=', date).orderBy(`dates.serviceDate`, 'asc')
      .limit(5).startAfter(doc)
    ).snapshotChanges().pipe(
      map(services => {
        return services.map(service => {
          const docRef = service.payload.doc;
          const data = service.payload.doc.data();
          const id = service.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // Obtiene el servicio mediante su id
  getServicesById(idDoc: string) {
    return this.angularFirestore.collection<any>(this.servicesRef).doc(idDoc).snapshotChanges().pipe(
      map(service => {
        const data: any = service.payload.data();
        const id = service.payload.id;
        return { id, ...data };
      }));
  }

  // Obtiene el servicio mediante su id sin subscription
  getServicesByIdWithOutSubscription(idDoc: string) {
    return this.angularFirestore.collection<any>(this.servicesRef).doc(idDoc).get();
  }

  // actualiza un servicio
  updateService(updateData: any, serviceId: string) {
    return this.angularFirestore.doc<any>(
      `${this.servicesRef}/${serviceId}`
    ).update(updateData);
  }

  // obtiene los servicios para el administrador
  getAdminServices(type: string, date: string) {
    return this.angularFirestore.collection<any>(
      this.servicesRef,
      ref => ref.where('type', '==', type).where('dates.serviceDate', '>=', date).orderBy(`dates.serviceDate`, 'asc').limit(10)
    ).snapshotChanges().pipe(
      map( services => {
        return services.map(service => {
          const docRef = service.payload.doc;
          const data = service.payload.doc.data();
          const id = service.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene más servicios para el administrador
  getMoreAdminServices(type: string, date: string, doc: any) {
    return this.angularFirestore.collection<any>(
      this.servicesRef,
      ref => ref.where('type', '==', type).where('dates.serviceDate', '>=', date).orderBy(`dates.serviceDate`, 'asc')
      .limit(5).startAfter(doc)
    ).snapshotChanges().pipe(
      map(services => {
        return services.map(service => {
          const docRef = service.payload.doc;
          const data = service.payload.doc.data();
          const id = service.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene los servicios para el administrador de Telemedicina
  getTelemedicinaServices(state: string) {
    return this.angularFirestore.collection<any>(
      'userProfile',
      ref => ref.where('telemedicina.state', '==', state).orderBy(`telemedicina.registerDate`, 'desc').limit(10)
    ).snapshotChanges().pipe(
      map( users => {
        return users.map( user => {
          const docRef = user.payload.doc;
          const data = user.payload.doc.data();
          const id = user.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene más servicios para el administrador de Telemedicina
  getMoreTelemedicinaServices(state: string, doc: any) {
    return this.angularFirestore.collection<any>(
      'userProfile',
      ref => ref.where('telemedicina.state', '==', state).orderBy(`telemedicina.registerDate`, 'desc')
      .limit(5).startAfter(doc)
    ).snapshotChanges().pipe(
      map( users => {
        return users.map( user => {
          const docRef = user.payload.doc;
          const data = user.payload.doc.data();
          const id = user.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene los servicios asignados a un mediamigo
  getMedifriendServices(type: string, date: string, uid: string) {
    return this.angularFirestore.collection<any>(
      this.servicesRef,
      ref => ref.where('mediuser.uid', '==', uid).where('type', '==', type)
              .where('dates.serviceDate', '>=', date)
              .orderBy(`dates.serviceDate`, 'asc').limit(10)
    ).snapshotChanges().pipe(
      map( services => {
        return services.map(service => {
          const docRef = service.payload.doc;
          const data = service.payload.doc.data();
          const id = service.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene más servicios asignados al mediamigo
  getMoreMedifriendServices(type: string, date: string, uid: string, doc: any) {
    return this.angularFirestore.collection<any>(
      this.servicesRef,
      ref => ref.where('mediuser.uid', '==', uid).where('type', '==', type)
            .where('dates.serviceDate', '>=', date).orderBy(`dates.serviceDate`, 'asc')
            .limit(5).startAfter(doc)
    ).snapshotChanges().pipe(
      map(services => {
        return services.map(service => {
          const docRef = service.payload.doc;
          const data = service.payload.doc.data();
          const id = service.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // agrega una dirección
  addUserLocation(location: any): Promise<DocumentReference> {
    return this.angularFirestore.collection<any>(this.userLocationRef).add(location);
  }

  // elimina una dirección
  deleteUserLocation(locationId: any): Promise<any> {
    return this.angularFirestore.collection<any>(this.userLocationRef).doc(locationId).delete();
  }

  // obtiene todos las direcciones de un usuario
  getUserLocations(user: string) {
    return this.angularFirestore.collection<any>(
      this.userLocationRef, ref => ref.where('user', '==', user)
    ).snapshotChanges().pipe(
      map(locations => {
        return locations.map(location => {
          const data = location.payload.doc.data();
          const id = location.payload.doc.id;
          return { id, ...data };
        });
      })
    );
  }

  // actualiza una user location
  updateUserLocation(updateData: any, locationId: string) {
    return this.angularFirestore.doc<any>(
      `${this.userLocationRef}/${locationId}`
    ).update(updateData);
  }

  /**
   * @description Carga las imágenes en una ruta en particular en el storage
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {FileTransport} transportData
   * @returns
   * @memberof ServiceService
   */
  public uploadFile(transportData: FileTransport) {
    return new Promise((resolve, reject) => {
      const filePath = `${transportData.metadata.folder}/${transportData.file.name}`;
      // Almacena la imagen
      this.storage.upload(filePath, transportData.file).then(snapshot => {
        // Obtiene la url de descarga
        snapshot.ref.getDownloadURL().then((downloadURL) => {
          resolve(downloadURL);
        });
      }).catch(error => {
        this.errorMonitoringService.sendError(error, 'uploadImage');
        reject();
      });
    });
  }

  /**
   * @description Borra un archivo de fire storage
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {FileTransport} transportData
   * @returns {Promise}
   * @memberof ServiceService
   */
  deleteFile(transportData: FileTransport): Promise<any> {
    return new Promise(() => {
      // elimina la imagen del storage
      this.storage.ref(
        `${transportData.folder}/${transportData.metadata.name}.${transportData.metadata.extension}`
      ).delete();
    });
  }

  // obtiene los servicios que tiene asignados un mediamigo
  // segun el servicio y la fecha
  getMedifriendAssignedServices(uid: string, date: any, type: string){
    return this.angularFirestore.collection<any>(
      this.servicesRef, ref => ref.where('mediuser.uid', '==', uid).where('type', '==', type).where('dates.serviceDate', '>', date))
      .snapshotChanges().pipe(
        map( services => {
          return services.map( service => {
            const docRef = service.payload.doc;
            const data = service.payload.doc.data();
            const id = service.payload.doc.id;
            return { id, ...data, docRef};
          });
        })
      );
  }

  // obtiene los vendedores de telemedicina
  getTelemedicinaMedifriends(medifriend: string){
    return this.angularFirestore.collection<any>(
      'userProfile', ref => ref.where('telemedicina.medifriend.uid', '==', medifriend))
      .snapshotChanges().pipe(
        map( services => {
          return services.map( service => {
            const docRef = service.payload.doc;
            const data = service.payload.doc.data();
            const id = service.payload.doc.id;
            return { id, ...data, docRef};
          });
        })
      );
  }

  /** Llama una función */
  callFunction(name: string, data: any) {
    const callable = this.angularFireFunctions.httpsCallable(name);
    return callable(data);
  }
}
